/**
 * Zweck: Beschreibt die TypeScript-Bausteine für den Frontend-Bereich frontend/src/app/services/app.warenkorb.service.ts.
 */
import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {AppConfigService} from './app.config.service';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {Warenkorb} from '../models/warenkorb';
import {WarenkorbEntry} from '../models/warenkorb-entry';
import {Artikel} from '../models/artikel';
import {AppEventService} from './app.event.service';
import {GuiMessage} from '../models/gui-message';
import {AppLieferkundeService} from './app.lieferkunde.service';
import {Lieferkunde} from '../models/lieferkunde';
import {AppPageloaderService} from './app.pageloader.service';
import {AppBestellungService} from './app.bestellung.service';
import {finalize, map} from 'rxjs/operators';
import {AppSettingsService} from './app.app_settings.service';
import {MerklistEntry} from '../models/merklist-entry';
import {BestellResult} from '../models/bestell-result';
import {TryAddArtikelToWkResponse} from '../modules/shop/models/try-add-artikel-to-wk-response';
import {WarenkorbEntryDtpZusatzdaten} from '../models/warenkorb-entry-dtp-zusatzdaten';
import {File} from '../models/file';
import setPrototypeOf = Reflect.setPrototypeOf;

/**
 * Zweck: Beschreibt class AppWarenkorbService und ihren Daten- oder Funktionsvertrag.
 */
@Injectable({providedIn: 'root'})
export class AppWarenkorbService implements OnDestroy {
  warenkoerbe = new BehaviorSubject<Warenkorb[]>([]);
  warenkoerbe$ = this.warenkoerbe.asObservable();

  primary_warenkorb = new BehaviorSubject<Warenkorb>(null);
  primary_warenkorb$ = this.primary_warenkorb.asObservable();

  artnr_wk_map = new BehaviorSubject<[][]>([]);
  artnr_wk_map$ = this.artnr_wk_map.asObservable();

  artnr_wk_map2 = new BehaviorSubject<any[]>([]);
  artnr_wk_map2$ = this.artnr_wk_map2.asObservable();

  lieferkunde: Lieferkunde;

  posUpdatePending = false;

  booted = new BehaviorSubject<boolean>(false);

  pwkid;

  pwksub: Subscription;
  esub: Subscription;

/**
 * Beschreibt Methode constructor und ihren Rückgabevertrag.
 */
  constructor(private http: HttpClient, private cfg: AppConfigService, private esvc: AppEventService,
              private lksvc: AppLieferkundeService, private loader: AppPageloaderService, private bsvc: AppBestellungService,
              private settsvc: AppSettingsService) {
    this.lksvc.current_lieferkunde$.subscribe(lk => {
      if (!this.lieferkunde || this.lieferkunde == null || !lk || this.lieferkunde.LieferkundeNr != lk.LieferkundeNr) {
        this.booted.next(false);
        this.primary_warenkorb.next(null);
        this.warenkoerbe.next([]);
      }
      this.lieferkunde = lk;
      if (this.lieferkunde) {
        this.load();

      } else {
        this.primary_warenkorb.next(null);
      }
    });

    this.esub = esvc.getQueue().subscribe(e => {
      if (e.name == 'App\\Events\\WarenkorbEngine\\WarenkorbChangedEvent') {
        if (!this.posUpdatePending) {
          this.load();
        }
      }
    });
    this.pwksub = this.primary_warenkorb.subscribe(wk => {
      if (wk) {
        this.pwkid = wk.id;

        if (this.pwkid !== 0) {
          this.settsvc.setUserSettingValue('GUI_PrimaryWarenkorbId', this.pwkid).subscribe();
        }
      }
    });
  }

/**
 * Beschreibt Methode init und ihren Rückgabevertrag.
 */
  init() {
    this.settsvc.getUserSettingValue('GUI_PrimaryWarenkorbId').subscribe(v => {
      this.pwkid = v;
      this.init_primary();

    });
  }

/**
 * Beschreibt Methode init_primary und ihren Rückgabevertrag.
 */
  init_primary() {
    if (this.warenkoerbe.value && this.pwkid !== undefined) {
      let found = false;
      this.warenkoerbe.value.forEach(wk => {
        if (wk.id == this.pwkid) {
          this.primary_warenkorb.next(wk);
          found = true;
        }
      });

      if (!found) {
        this.primary_warenkorb.next(this.warenkoerbe.value[0]);
      }

      if (!this.booted.value) {
        this.booted.next(true);
      }
    }
  }

/**
 * Beschreibt Methode ngOnDestroy und ihren Rückgabevertrag.
 */
  ngOnDestroy(): void {
    if (this.esub instanceof Subscription) {
      this.esub.unsubscribe();
    }
    if (this.pwksub instanceof Subscription) {
      this.pwksub.unsubscribe();
    }
  }


/**
 * Beschreibt Methode silentLoadArtNrMapping und ihren Rückgabevertrag.
 */
  silentLoadArtNrMapping() {
    this.getArtNrMapping().subscribe(m => {
      this.artnr_wk_map.next(m);
      this.artnr_wk_map2.next(Object.keys(m));
    });
  }

/**
 * Beschreibt Methode load und ihren Rückgabevertrag.
 */
  load() {
    this.loader.start('AppWarenkorbService_1');
    this.getWarenkoerbe().subscribe(wks => {
      this.warenkoerbe.next(wks);
      if (this.primary_warenkorb.getValue() === null || this.primary_warenkorb.getValue() === undefined) {
        this.init();
      } else {
        let found = false;
        const id = this.primary_warenkorb.getValue().id;
        wks.forEach(wk => {
          if (wk.id == id) {
            this.primary_warenkorb.next(wk);
            found = true;
          }
        });

        if (!found) {
          this.primary_warenkorb.next(wks[0]);


          if (!this.booted.value) {
            this.booted.next(true);
          }
        }
      }

      this.loader.stop('AppWarenkorbService_1');
    });

    this.silentLoadArtNrMapping();
  }

/**
 * Beschreibt Methode sendWarenkorb und ihren Rückgabevertrag.
 */
  createFastOrderWarenkorb(): Observable<Warenkorb> {
    return this.http.post<Warenkorb>('/warenkorb/fast-order/create', {});
  }

/**
 * Sendet den Warenkorb und liefert die einzelnen Bestellergebnisse.
 */
  sendWarenkorb(warenkorb: Warenkorb, confirmationMailCopyTo: string[] = []): Observable<{default: BestellResult, dtp: BestellResult[]}> {
    return this.http.post<{default: BestellResult, dtp: BestellResult[]}>(
      '/warenkorb/' + warenkorb.id + '/send',
      {
        confirmationMailCopyTo: confirmationMailCopyTo,
        reTage: warenkorb.reTage,
        warenguthabenauftrag: warenkorb.warenguthabenauftrag,
      },
    ).pipe(map(result => {
      if (result.default && result.default.bestellung) {
        this.bsvc.protoBestellung(result.default.bestellung);
      }
      if (result.dtp) {
        result.dtp.forEach(dtpres => {
          if (dtpres.bestellung) {
            this.bsvc.protoBestellung(dtpres.bestellung);
          }
        });
      }
      return result;
    }));
  }

/**
 * Beschreibt Methode createWarenkorb und ihren Rückgabevertrag.
 */
  createWarenkorb(warenkorb: Warenkorb): Observable<Warenkorb> {
    return this.http.post<Warenkorb>(
      '/warenkorb/add',
      {
        warenkorb: warenkorb
      }
    ).pipe(map(wk => {
      this.protoWarenkorb(wk);
      return wk;
    }));
  }

/**
 * Beschreibt Methode adminGetLocked und ihren Rückgabevertrag.
 */
  adminGetLocked(): Observable<Warenkorb[]> {
    return this.http.get<Warenkorb[]>(
      '/warenkorb/locked',
    ).pipe(map(wks => {
      wks.forEach(wk => {
        this.protoWarenkorb(wk);
      });

      return wks;
    }));
  }

/**
 * Beschreibt Methode adminUnlock und ihren Rückgabevertrag.
 */
  adminUnlock(wk: Warenkorb): Observable<boolean> {
    return this.http.get<boolean>(
      '/warenkorb/' + wk.id + '/unlock',
    );
  }

/**
 * Beschreibt Methode moveFromToWarenkorb und ihren Rückgabevertrag.
 */
  moveFromToWarenkorb(warenkorbFrom: Warenkorb, warenkorbTo: Warenkorb, entries: WarenkorbEntry[]): Observable<boolean> {
    const entryIds = [];
    entries.forEach(e => {
      entryIds.push(e.id);
    });
    return this.http.post<boolean>(
      '/warenkorb/' + warenkorbFrom.id + '/move/' + warenkorbTo.id,
      {
        ids: entryIds
      }
    );
  }

/**
 * Beschreibt Methode updateWarenkorb und ihren Rückgabevertrag.
 */
  updateWarenkorb(warenkorb: Warenkorb): Observable<Warenkorb> {
    return this.http.post<Warenkorb>(
      '/warenkorb/' + warenkorb.id + '/update',
      {
        warenkorb: warenkorb
      }
    ).pipe(map(wk => {
      this.protoWarenkorb(wk);
      return wk;
    }));
  }

/**
 * Beschreibt Methode deleteWarenkorb und ihren Rückgabevertrag.
 */
  deleteWarenkorb(warenkorb: Warenkorb): Observable<GuiMessage> {
    return this.http.post<GuiMessage>(
      '/warenkorb/' + warenkorb.id + '/delete',
      {}
    );
  }

/**
 * Beschreibt Methode emptyWarenkorb und ihren Rückgabevertrag.
 */
  emptyWarenkorb(warenkorb: Warenkorb): Observable<GuiMessage> {
    return this.http.post<GuiMessage>(
      '/warenkorb/' + warenkorb.id + '/empty',
      {}
    );
  }

/**
 * Beschreibt Methode getWarenkoerbe und ihren Rückgabevertrag.
 */
  getWarenkoerbe(show_all = false, only_with_positions = false): Observable<Warenkorb[]> {
    return this.http.get<Warenkorb[]>(
      '/warenkorb?all_lieferkunden=' + (show_all ? 't' : 'f') + '&only_with_positions=' + (only_with_positions ? 't' : 'f')
    ).pipe(map(wks => {
      wks.forEach(wk => {
        this.protoWarenkorb(wk);
      });
      return wks;
    }));
  }

/**
 * Beschreibt Methode getArtNrMapping und ihren Rückgabevertrag.
 */
  getArtNrMapping(): Observable<[][]> {
    return this.http.get<[][]>(
      '/warenkorb/artNrMapping'
    );
  }

/**
 * Beschreibt Methode getWarenkorbById und ihren Rückgabevertrag.
 */
  getWarenkorbById(id): Observable<Warenkorb> {
    return this.http.get<Warenkorb>(
      '/warenkorb/' + id
    ).pipe(map(wk => {
      this.protoWarenkorb(wk);
      return wk;
    }));
  }

/**
 * Beschreibt Methode exportWarenkorbById und ihren Rückgabevertrag.
 */
  exportWarenkorbById(id): Observable<any> {
    return this.http.get(
      '/warenkorb/' + id + '/export',
      {
        responseType: 'blob',
        reportProgress: true,
        observe: 'events',
        headers: new HttpHeaders({ 'ngsw-bypass': 'true' })
      }
    );
  }

/**
 * Beschreibt Methode getWarenkorbForCheckoutById und ihren Rückgabevertrag.
 */
  getWarenkorbForCheckoutById(id): Observable<Warenkorb> {
    return this.http.get<Warenkorb>(
      '/warenkorb/' + id + '/checkout'
    ).pipe(map(wk => {
      this.protoWarenkorb(wk);
      return wk;
    }));
  }

/**
 * Beschreibt Methode addToWarenkorbById und ihren Rückgabevertrag.
 */
  addToWarenkorbById(id, view_slug, entry: WarenkorbEntry | MerklistEntry, actionSource: string = null): Observable<GuiMessage> {
    return this.http.post<GuiMessage>(
      '/warenkorb/' + id + '/add',
      {
        view_slug: view_slug,
        entry: entry,
        actionSource: actionSource
      }
    );
  }

/**
 * Beschreibt Methode tryAddToWarenkorbById und ihren Rückgabevertrag.
 */
  tryAddToWarenkorbById(id, view_slug, entry: WarenkorbEntry | MerklistEntry): Observable<TryAddArtikelToWkResponse> {
    return this.http.post<TryAddArtikelToWkResponse>(
      '/warenkorb/' + id + '/tryAdd',
      {
        view_slug: view_slug,
        entry: entry
      }
    );
  }

/**
 * Beschreibt Methode addMultiToWarenkorbById und ihren Rückgabevertrag.
 */
  addMultiToWarenkorbById(id, entries: WarenkorbEntry[] | MerklistEntry[], actionSource: string = null): Observable<GuiMessage[]> {
    return this.http.post<GuiMessage[]>(
      '/warenkorb/' + id + '/addMulti',
      {
        entries: entries,
        actionSource: actionSource
      }
    );
  }

/**
 * Beschreibt Methode deleteFromWarenkorbById und ihren Rückgabevertrag.
 */
  deleteFromWarenkorbById(id, posid): Observable<GuiMessage> {
    return this.http.post<GuiMessage>(
      '/warenkorb/' + id + '/' + posid + '/delete',
      {}
    );
  }

/**
 * Beschreibt Methode duplicateFromWarenkorbById und ihren Rückgabevertrag.
 */
  duplicateFromWarenkorbById(id, posid): Observable<GuiMessage> {
    return this.http.post<GuiMessage>(
      '/warenkorb/' + id + '/' + posid + '/duplicate',
      {}
    );
  }

/**
 * Beschreibt Methode updateForWarenkorbById und ihren Rückgabevertrag.
 */
  updateForWarenkorbById(id, entry: WarenkorbEntry): Observable<GuiMessage> {
    this.posUpdatePending = true;
    return this.http.post<GuiMessage>(
      '/warenkorb/' + id + '/' + entry.id + '/update',
      {
        entry: entry
      }
    ).pipe(finalize(() => {
      this.posUpdatePending = false;
    }));
  }

/**
 * Beschreibt Methode saveDTPZusatzdatenData und ihren Rückgabevertrag.
 */
  saveDTPZusatzdatenData(wkId, id, data): Observable<WarenkorbEntryDtpZusatzdaten> {
    return this.http.post<WarenkorbEntryDtpZusatzdaten>(
      '/warenkorb/' + wkId + '/setDTPZusatzdaten/' + id,
      {
        data: data
      }
    );
  }

/**
 * Beschreibt Methode clearDTPZusatzdatenData und ihren Rückgabevertrag.
 */
  clearDTPZusatzdatenData(wkId, id): Observable<WarenkorbEntryDtpZusatzdaten> {
    return this.http.post<WarenkorbEntryDtpZusatzdaten>(
      '/warenkorb/' + wkId + '/clearDTPZusatzdaten/' + id,
      {

      }
    );
  }

/**
 * Beschreibt Methode uploadDTPZusatezdatenFile und ihren Rückgabevertrag.
 */
  uploadDTPZusatezdatenFile(wkId, id, file: File, dsgvoAutoBlackening, dsgvoManualBlackening) {
    const options: any = {
      reportProgress: true,
      observe: 'events',
      headers: new HttpHeaders({ 'ngsw-bypass': 'true' })
    };

    const formData: FormData = new FormData();
    formData.append('file', file.rawFile);
    formData.append('dsgvoAutoBlackening', dsgvoAutoBlackening);
    formData.append('dsgvoManualBlackening', dsgvoManualBlackening);

    return this.http.post<any>(
      '/warenkorb/' + wkId + '/setDTPZusatzdaten/' + id,
      formData,
      options
    );
  }

/**
 * Liefert die gespeicherte Datei einer DTP-Zusatzangabe als Blob.
 */
  getDTPZusatzdatenFile(wkId, id): Observable<Blob> {
    return this.http.get(
      '/warenkorb/' + wkId + '/getDTPZusatzdatenFile/' + id,
      {responseType: 'blob'}
    );
  }

  private protoWarenkorb(wk: Warenkorb) {
    if (wk) {
      setPrototypeOf(wk, Warenkorb.prototype);

      if (wk.entries) {
        wk.entries.forEach(e => {
          setPrototypeOf(e, WarenkorbEntry.prototype);

          if (e.dtpZusatzdaten) {
            e.dtpZusatzdaten.forEach(dtpz => {
              setPrototypeOf(dtpz, WarenkorbEntryDtpZusatzdaten.prototype);
            });
          }

          if (e.artikel) {
            setPrototypeOf(e.artikel, Artikel.prototype);
          }
        });
      }
    }
  }
}
